import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const Row = ({ k, v, id }) => (
  <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-stone-100">
    <span className="text-stone-500">{k}</span><span className="font-bold text-right" data-testid={id}>{v}</span>
  </div>
);

export default function AdminAi() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [denied, setDenied] = useState(false);
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get("/admin/ai-config").then((r) => setCfg(r.data)).catch(() => setDenied(true)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (on) => {
    const confirm = on ? window.prompt("Type ENABLE to turn the admin AI toggle on. Live calls still need AI_LIVE_CALLS_ENABLED=true on the server.") : null;
    if (on && confirm !== "ENABLE") { if (confirm !== null) toast.error("Not enabled — you must type ENABLE exactly"); return; }
    try { await api.put("/admin/ai-config", { admin_enabled: on, reason, confirm }); toast.success("Saved · audit logged"); setReason(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed"); }
  };
  if (denied) return <div className="max-w-xl mx-auto p-6 text-sm" data-testid="adminai-denied">{t("adminai.denied", "Admins only.")}</div>;
  if (!cfg) return <div className="max-w-xl mx-auto p-6 text-sm">Loading…</div>;
  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-4" data-testid="admin-ai-page">
      <h1 className="text-2xl font-black">{t("adminai.title", "AI configuration")}</h1>
      <p className="text-xs text-stone-500">{t("adminai.desc", "Live AI calls run only when BOTH the admin toggle and the server kill switch are on. Otherwise AINA and search use the rule-based engine. No provider is active or paid for.")}</p>
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <Row k={t("adminai.engine", "Active engine")} v={cfg.active_engine === "llm" ? t("adminai.engine_llm", "LLM (live)") : t("adminai.engine_rules", "Rule-based (no AI calls)")} id="adminai-engine" />
        <Row k={t("adminai.admin_toggle", "Admin toggle (database)")} v={cfg.admin_enabled ? "ON" : "OFF"} id="adminai-admin-flag" />
        <Row k={t("adminai.env_switch", "Server kill switch") + " (AI_LIVE_CALLS_ENABLED)"} v={cfg.env_switch_enabled ? "ON" : "OFF"} id="adminai-env-flag" />
        <Row k={t("adminai.candidate", "Future model candidate")} v={`${cfg.provider} · ${cfg.model} (${t("adminai.inactive", "inactive")})`} id="adminai-model" />
        <Row k={t("adminai.applies", "Applies to")} v={cfg.applies_to.join(", ")} id="adminai-scope" />
        <Row k={t("adminai.cost", "Cost approved")} v={cfg.cost_approved ? t("common.yes", "Yes") : t("common.no", "No")} id="adminai-cost" />
      </div>
      <div className="rounded-2xl border border-stone-200 bg-white p-4 space-y-2">
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("adminai.reason_ph", "Reason for the change (required, for the audit log)")}
               className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm" data-testid="adminai-reason" />
        <div className="flex gap-2">
          <button disabled={reason.trim().length < 5 || cfg.admin_enabled} onClick={() => save(true)} data-testid="adminai-enable"
                  className="h-10 px-4 rounded-full bg-stone-900 text-white font-bold disabled:opacity-40">{t("adminai.turn_on", "Turn admin toggle ON")}</button>
          <button disabled={reason.trim().length < 5 || !cfg.admin_enabled} onClick={() => save(false)} data-testid="adminai-disable"
                  className="h-10 px-4 rounded-full border border-stone-300 font-bold disabled:opacity-40">{t("adminai.turn_off", "Turn OFF")}</button>
        </div>
      </div>
      <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="adminai-audit">
        <div className="text-xs font-black uppercase tracking-widest text-stone-500 mb-2">{t("adminai.audit", "Audit log")}</div>
        {cfg.audit.length === 0 ? <div className="text-sm text-stone-500">{t("adminai.no_changes", "No changes yet.")}</div> : cfg.audit.map((a) => (
          <div key={a.audit_id} className="text-xs py-1 border-b border-stone-100" data-testid={`adminai-audit-${a.audit_id}`}>
            {new Date(a.at).toLocaleString()} · {a.actor_name || a.actor_id} · {a.before.admin_enabled ? "ON" : "OFF"} → {a.after.admin_enabled ? "ON" : "OFF"} · {a.reason}
          </div>
        ))}
      </div>
    </div>
  );
}
